'use client';

import { FC } from 'react';
import Image from 'next/image';
import FadeIn from '@/components/FadeIn/FadeIn';
import { ComparisonData, ComparisonFeature } from '../types';

interface FeatureTableProps {
  data: ComparisonData;
}

// Filled circle with checkmark - matches Figma design
const CheckIcon: FC = () => (
  <div className="w-7 h-7 rounded-full bg-green-500 flex items-center justify-center">
    <svg className="w-4 h-4 text-white" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    </svg>
  </div>
);

// Filled circle with X - matches Figma design
const XIcon: FC = () => (
  <div className="w-7 h-7 rounded-full bg-orange-500 flex items-center justify-center">
    <svg className="w-4 h-4 text-white" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
        clipRule="evenodd"
      />
    </svg>
  </div>
);

const FeatureValue: FC<{ value: boolean | string }> = ({ value }) => {
  if (value === true) {
    return <CheckIcon />;
  }
  if (value === false) {
    return <XIcon />;
  }
  return (
    <span className="text-amber-600 font-medium text-sm px-2 py-1 bg-amber-50 rounded">
      {value}
    </span>
  );
};

// MarketBetter logo component - full logo includes icon
const MBLogo: FC = () => (
  <div className="flex items-center justify-center">
    <Image
      src="/images/compare/mb-full-logo.png"
      alt="marketbetter.ai"
      width={160}
      height={32}
      className="h-8 w-auto object-contain"
    />
  </div>
);

// Unify logo component - full logo includes icon
const UnifyLogo: FC = () => (
  <div className="flex items-center justify-center">
    <Image
      src="/images/compare/unify-full-logo.png"
      alt="Unify"
      width={120}
      height={32}
      className="h-7 w-auto object-contain"
    />
  </div>
);

// Common Room logo component - full logo includes icon
const CommonRoomLogo: FC = () => (
  <div className="flex items-center justify-center">
    <Image
      src="/images/compare/common-full-logo.png"
      alt="Common Room"
      width={140}
      height={32}
      className="h-7 w-auto object-contain"
    />
  </div>
);

const getCompetitorLogo = (name: string) => {
  const normalized = name.toLowerCase();
  if (normalized === 'unify') return <UnifyLogo />;
  if (normalized === 'common room') return <CommonRoomLogo />;
  return <span className="font-medium text-slate-700">{name}</span>;
};

const FeatureTable: FC<FeatureTableProps> = ({ data }) => {
  return (
    <section className="py-16 md:py-24">
      <div
        className="container-fluid px-4 md:px-7.5 mx-auto"
        style={{ maxWidth: 'clamp(1000px, 70vw, 1200px)' }}
      >
        <FadeIn variant="up" delay={0.1}>
          {/* Section Title */}
          <div className="text-center mb-12">
            <h2
              className="font-medium text-black"
              style={{ fontSize: 'clamp(1.5rem, 2.5vw, 2.5rem)' }}
            >
              Why{' '}
              <span style={{ color: '#094FFE' }}>look</span> for a {data.competitor.name}
              <br />
              alternative?
            </h2>
          </div>

          {/* Comparison Table */}
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
            {/* Table Header */}
            <div className="grid grid-cols-3 border-b border-slate-200">
              <div className="py-5 px-6 font-medium text-slate-600">Features</div>
              <div className="py-5 px-6 flex justify-center">
                <MBLogo />
              </div>
              <div className="py-5 px-6 flex justify-center">
                {getCompetitorLogo(data.competitor.name)}
              </div>
            </div>

            {/* Table Rows */}
            {data.features.map((feature: ComparisonFeature, index: number) => (
              <div
                key={index}
                className={`grid grid-cols-3 border-b border-slate-100 last:border-b-0 ${
                  index % 2 === 0 ? 'bg-slate-50/50' : 'bg-white'
                }`}
              >
                <div className="py-4 px-6 text-slate-700">{feature.name}</div>
                <div className="py-4 px-6 flex justify-center items-center">
                  <FeatureValue value={feature.marketbetter} />
                </div>
                <div className="py-4 px-6 flex justify-center items-center">
                  <FeatureValue value={feature.competitor} />
                </div>
              </div>
            ))}
          </div>
        </FadeIn>
      </div>
    </section>
  );
};

export default FeatureTable;
